import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import { useNotifications } from '../../hooks/useNotifications';
import { useBroadcasts } from '../../contexts/BroadcastsContext';
import DriverTabBar from '../../components/driver/DriverTabBar';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS, FONTS } from '../../styles/tokens';

function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const ms = Date.now() - (timestamp.toMillis ? timestamp.toMillis() : timestamp);
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'Akkurat nå';
  if (minutes < 60) return `${minutes} min siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} t siden`;
  const days = Math.floor(hours / 24);
  return `${days} d siden`;
}

// Hvor et trykk på varselet fører, etter rolle og type. Bestiller går til oppdraget når orderId finnes.
const CARRIER_ORDER_COMPANY_TYPES = ['carrier-order-accepted', 'carrier-order-declined', 'carrier-order-completed'];

export function getNavPath(notif, role) {
  // Transportør-varsler: invitasjonen kan komme til en bestiller eller sjåfør, så den går foran rollen
  if (notif.type === 'carrier-invite') return `/invitasjon/transportor/${notif.orgId}`;
  if (notif.type === 'carrier-vehicle-alert') return `/transportor/biler/${notif.vehicleId}`;
  // Egne ordre: målet følger varseltypen, ikke rollen (en bruker kan være både ansatt og sjåfør).
  // Svar og fullføring går til ordren hos firmaet; tilbud og endringer til sjåførens Mine oppdrag.
  if (notif.type?.startsWith('carrier-order-')) {
    return CARRIER_ORDER_COMPANY_TYPES.includes(notif.type) ? `/transportor/ordre/${notif.orderId}` : '/driver?tab=mine';
  }
  if (role === 'carrier') return '/transportor';
  if (role === 'orderer') {
    if (notif.type === 'support-reply') return '/orderer/tilbakemelding';
    if (notif.orderId) return `/orderer/order/${notif.orderId}`;
    if (notif.type?.startsWith('invoice')) return '/orderer/invoices';
    if (notif.type?.startsWith('org')) return '/orderer/org';
    return '/orderer';
  }
  if (notif.type === 'support-reply') return '/driver/tilbakemelding';
  if (notif.type === 'order-cancelled') return '/driver/orders';
  if (notif.type === 'fee-overdue') return '/driver/earnings';
  return '/driver';
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { notifications, loading, markAsRead } = useNotifications();
  const { inboxBroadcasts, dismiss } = useBroadcasts();
  const { effectiveRole } = useAuth();
  const empty = !loading && notifications.length === 0 && inboxBroadcasts.length === 0;

  function handleClick(notif) {
    if (!notif.read) markAsRead(notif.id);
    navigate(getNavPath(notif, effectiveRole));
  }

  return (
    <div className="orderer-ui driver-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
        <h1 style={styles.heading}>Varsler</h1>
        {loading && <p style={styles.empty}>Laster…</p>}
        {empty && <p style={styles.empty}>Ingen varsler ennå.</p>}
        {!loading && !empty && (
          <div style={styles.list}>
            <div style={{ marginBottom: -1 }}>
            {inboxBroadcasts.map(bc => (
              <button key={bc.id} type="button" style={styles.item} onClick={() => dismiss(bc.id)}>
                <span style={styles.icon} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4 14h7l-1 8 9-12h-7z" /></svg>
                </span>
                <span style={styles.text}>
                  <span style={styles.title}>{bc.title}<span style={styles.tippiBadge}>Fra Tippi</span></span>
                  <span style={styles.body}>{bc.body}</span>
                </span>
                <span style={styles.side}>
                  <span style={styles.dot} aria-label="Ulest" />
                </span>
              </button>
            ))}
            {notifications.map(notif => (
              <button key={notif.id} type="button" style={styles.item} onClick={() => handleClick(notif)}>
                <span style={styles.icon} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6h11v10H2zM13 9h4l3 3v4h-7" /><circle cx="6" cy="17.5" r="1.8" /><circle cx="16.5" cy="17.5" r="1.8" /></svg>
                </span>
                <span style={styles.text}>
                  <span style={{ ...styles.title, fontWeight: notif.read ? 600 : 700 }}>{notif.title}</span>
                  <span style={styles.body}>{notif.body}</span>
                </span>
                <span style={styles.side}>
                  <span style={styles.time}>{formatRelativeTime(notif.createdAt).toUpperCase()}</span>
                  {notif.read ? <span style={styles.dotSpace} /> : <span style={styles.dot} aria-label="Ulest" />}
                </span>
              </button>
            ))}
            </div>
          </div>
        )}
      </div>
      {effectiveRole === 'driver' && <DriverTabBar />}
    </div>
  );
}

const styles = {
  root: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: COLORS.bg },
  page: { maxWidth: 600, margin: '0 auto', padding: '24px 20px', width: '100%', flex: 1, paddingTop: 'max(32px, env(safe-area-inset-top))' },
  heading: { fontFamily: FONTS.display, fontSize: 40, lineHeight: 1, fontWeight: 700, margin: '12px 0 8px', color: COLORS.textDark },
  empty: { color: COLORS.textLight, textAlign: 'center', marginTop: 40 },
  list: { display: 'flex', flexDirection: 'column', background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: '2px 16px', marginTop: 14, overflow: 'hidden' },
  item: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '13px 0',
    background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`,
    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: COLORS.textDark,
  },
  icon: { width: 42, height: 42, borderRadius: 12, background: '#efeadd', color: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  text: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  title: { fontSize: 15, color: COLORS.textDark, fontWeight: 700 },
  side: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 },
  time: { fontFamily: 'var(--font-mono)', fontSize: 10, color: COLORS.textLight, letterSpacing: '0.03em', whiteSpace: 'nowrap' },
  body: { fontSize: 13.5, color: COLORS.textMid },
  dot: { width: 9, height: 9, borderRadius: '50%', background: COLORS.accent },
  dotSpace: { width: 9, height: 9 },
  tippiBadge: {
    display: 'inline-block', fontSize: 10, fontWeight: 700,
    color: '#1a1a2e', background: '#efeadd', borderRadius: 20,
    padding: '2px 8px', marginLeft: 8, textTransform: 'uppercase', letterSpacing: 0.4,
    verticalAlign: 'middle',
  },
};
